/* eslint-disable consistent-return */
import React, { useState, useEffect } from 'react';
import {
  FilterContext,
  FilterContextValue,
  useBreakpoint,
  useServicesContext,
  trackShowMoreOptions,
  dynamicStylingHelper,
  dynamicStyling,
  isLegacy,
} from '@oneaudi/vtp-shared';
import { QuickModelCheckbox } from './QuickModelCheckbox';
import { MoreOptionsButtonsWrapper, FieldsetOneCol } from '../styles/QuickModelCheckbox.styles';
import { useI18n } from '@oneaudi/i18n-context';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { TextLink, TextLinkProps } from '@oneaudi/unified-web-components';
import { SubHeadText } from '../styles/SwitchNCUC.styles';
import { SYS_SPACE_RELATIVE_SM } from '@oneaudi/unified-web-alpha-common';
import styled from 'styled-components';

export interface QuickModelFilterProps {
  filterCategory: string;
}

export const QuickModelFilter: React.FC<QuickModelFilterProps> = ({ filterCategory }) => {
  const context = React.useContext(FilterContext) as FilterContextValue;
  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;

  const { modelFilterData } = context;

  const headlineLabel = useI18n({
    id: `nemo.sc.plp.quickfilters.modelsHeadline`,
    defaultMessage: 'Models',
  });
  const showMoreOptionsLabel = useI18n({
    id: `nemo.sc.plp.quickfilters.showmoreoptions`,
    defaultMessage: 'Show more options',
  });
  const hideOptionsLabel = useI18n({
    id: `nemo.sc.plp.quickfilters.hideoptions`,
    defaultMessage: 'Hide options',
  });

  const breakpoint = useBreakpoint();

  const [maxNumberOfCheckboxesToDisplay, setMaxNumberOfCheckboxesToDisplay] = useState<number>(0);
  const [showAllCheckboxes, setShowAllCheckboxes] = useState<boolean>(false);

  const handleShowMoreCheckboxes = () => {
    setShowAllCheckboxes(true);
    trackShowMoreOptions(trackingService, showMoreOptionsLabel, filterCategory);
  };

  const handleShowFewerCheckboxes = () => {
    setShowAllCheckboxes(false);
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    // eslint-disable-next-line default-case
    switch (breakpoint) {
      case 'XS':
        setMaxNumberOfCheckboxesToDisplay(3);
        break;
      case 'S':
        setMaxNumberOfCheckboxesToDisplay(3);
        break;
      case 'M':
        setMaxNumberOfCheckboxesToDisplay(6);
        break;
      case 'L':
        setMaxNumberOfCheckboxesToDisplay(3);
        break;
      case 'XL':
        setMaxNumberOfCheckboxesToDisplay(3);
        break;
      case 'XXL':
        setMaxNumberOfCheckboxesToDisplay(isLegacy ? 3 : 6);
        break;
    }
  }, [breakpoint]);

  if (!modelFilterData) return;
  const numberOfAdditionalCheckboxes: number =
    Object.keys(modelFilterData.carlineGroupsStructure).length - maxNumberOfCheckboxesToDisplay;

  return (
    <div>
      <SubHeadText
        variant={{ type: 'copy', order: dynamicStylingHelper('2', '1'), style: 'short' }}
        margin={[0, 0, dynamicStylingHelper('12px', SYS_SPACE_RELATIVE_SM), 0]}
      >
        {headlineLabel}
      </SubHeadText>
      <FieldsetOneCol>
        {Object.entries(modelFilterData.carlineGroupsStructure).map((key: any, index: number) => {
          if (index < maxNumberOfCheckboxesToDisplay || showAllCheckboxes) {
            return (
              <QuickModelCheckbox
                groupHeadline="carline"
                carlineGroup={key[0]}
                filterCategory={filterCategory}
                carlinesGroupMembers={key[1].carlines}
                itemText={key[1].text}
                key={key[0]}
              />
            );
          }
          return '';
        })}
      </FieldsetOneCol>
      <MoreOptionsButtonsWrapper>
        {numberOfAdditionalCheckboxes > 0 && !showAllCheckboxes && (
          <StyledTextLink
            icon="add"
            iconPosition="leading"
            size="sm"
            variant="standalone"
            onClick={handleShowMoreCheckboxes}
          >
            {showMoreOptionsLabel}
          </StyledTextLink>
        )}
        {numberOfAdditionalCheckboxes > 0 && showAllCheckboxes && (
          <StyledTextLink
            icon="remove"
            iconPosition="leading"
            size="sm"
            variant="standalone"
            onClick={handleShowFewerCheckboxes}
          >
            {hideOptionsLabel}
          </StyledTextLink>
        )}
      </MoreOptionsButtonsWrapper>
    </div>
  );
};

export default QuickModelFilter;

const StyledTextLink = styled((props) => <TextLink {...props} />)<TextLinkProps>`
  ${dynamicStyling({
    alpha: `
      text-decoration: none;
  `,
  })};
`;
